import React, { useState } from 'react';
import styled from '@emotion/styled';
import { doc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  LINE_STATUSES,
  SIZES,
  sortSizes,
  LINE_TYPES
} from '../firebase/schema';
import { formatDate } from '../utils/dateFormatter';
import { FaTimes } from 'react-icons/fa';
import { getMockupUrl } from '../utils/fileHelpers';
import { syncOrderStatusFromLines, maybeFinalizeSampleOrderAfterReceive } from '../utils/orderStatusSync';
import { isSampleLine } from '../utils/sampleOrder';
import { lineHasBeenOrdered, lineIsFinalized, getEffectiveLineStatus } from '../utils/lineCutStatus';
import { activityLogAppend } from '../utils/lineActivityLog';
import { reasonSpLineCannotPrint } from '../utils/spLicenseStore';

// Map DB/stored type (e.g. 'screen', 'dtf') to LINE_TYPES display value for the select
const getDisplayType = (type) => {
  if (!type) return LINE_TYPES.SCREEN_PRINT;
  const t = String(type).toLowerCase();
  if (t === 'screen' || t === 'screen print') return LINE_TYPES.SCREEN_PRINT;
  if (t === 'embroidery') return LINE_TYPES.EMBROIDERY;
  if (t === 'dtg') return LINE_TYPES.DTG;
  if (t === 'dtf') return LINE_TYPES.DTF;
  if (t === 'design_only' || t === 'design only') return LINE_TYPES.DESIGN_ONLY;
  if (Object.values(LINE_TYPES).includes(type)) return type;
  return LINE_TYPES.SCREEN_PRINT;
};

// Map LINE_TYPES display value back to DB format for saving
const getDbType = (displayType) => {
  if (!displayType) return 'screen';
  const v = String(displayType).toLowerCase();
  if (v === 'screen print') return 'screen';
  if (v === 'design only') return 'design_only';
  if (['embroidery', 'dtg', 'dtf', 'screen'].includes(v)) return v;
  return 'screen';
};

const LineContainer = styled.div`
  background: var(--al-surface);
  border: 1px solid ${props => props.theme.colors.border};
  padding: 8px 12px;
  margin-bottom: 4px;
  display: grid;
  grid-template-columns: 40px 130px 160px 180px 140px 220px 100px 120px;
  gap: 8px;
  align-items: start;
  font-family: 'Montserrat', sans-serif;
  color: white;
  width: 100%;
  text-transform: uppercase;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  height: 100%;
`;

const Label = styled.div`
  font-size: 0.65em;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 4px;
`;

const Value = styled.div`
  font-size: 0.85em;
  color: white;
  font-weight: 500;
  text-transform: uppercase;
`;

const MockupContainer = styled.div`
  width: 150px;
  height: 150px;
  overflow: hidden;
  background: ${props => props.noImage ? '#FF7F7F' : '#363636'};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.noImage ? 'default' : 'pointer'};
  transition: transform 0.2s;

  &:hover {
    transform: ${props => props.noImage ? 'none' : 'scale(1.05)'};
  }
`;

const MockupImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LargeArtworkModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LargeArtworkContent = styled.div`
  width: 95vw;
  height: 95vh;
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const LargeArtworkImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 20px;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const InkColorsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 0.75em;
  color: white;
  text-transform: uppercase;
`;

const CustomCheckbox = styled.input`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
  box-shadow: none;
  outline: none;
  
  &:checked {
    background: ${props => props.theme.colors.primary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
`;

const CheckboxLabel = styled.span`
  color: white;
  font-size: 0.85em;
  text-transform: uppercase;
`;

const SizeBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SizeRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const SizeUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 20px;
  
  input {
    width: 100%;
    padding: 2px 4px;
    background: #363636;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 0;
    color: white;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    box-shadow: none;
    outline: none;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
    
    /* Chrome-specific resets */
    &::-webkit-input-placeholder {
      opacity: 1;
    }
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    
    &[type=number] {
      -moz-appearance: textfield;
    }
  }
  
  .size-label {
    font-size: 0.65em;
    color: ${props => props.theme.colors.textDim};
    margin-bottom: 2px;
  }
  
  .size-qty {
    font-size: 0.75em;
    color: ${props => props.theme.colors.text};
    font-weight: 500;
  }
`;

const InkColorCount = styled.div`
  font-size: 0.85em;
  color: white;
  font-weight: 500;
  text-transform: uppercase;
`;

const StatusBadge = styled.div`
  font-size: 0.7em;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => {
    switch (props.status) {
      case LINE_STATUSES.NEW:
        return '#4A4A4A';
      case LINE_STATUSES.ORDERED:
        return '#5B9BD5';
      case LINE_STATUSES.IN_NOT_CUT:
      case LINE_STATUSES.CUT_NOT_IN:
        return '#FFA500';
      case LINE_STATUSES.READY_TO_PRINT:
        return 'var(--al-primary)';
      case LINE_STATUSES.PRINTED:
      case LINE_STATUSES.COMPLETED:
        return '#4CAF50';
      default:
        return '#4A4A4A';
    }
  }};
  color: white;
  text-transform: uppercase;
  font-weight: 600;
  text-align: center;
  width: fit-content;
`;

const Select = styled.select`
  background: #363636;
  border: 1px solid ${props => props.theme.colors.border};
  color: white;
  padding: 4px 8px;
  font-size: 0.85em;
  width: 100%;
  text-transform: uppercase;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  option {
    background: #363636;
    color: white;
  }
`;

const LineItemCompact = ({ line, onLineUpdate, isAdmin = false, products = [] }) => {
  const [showOverride, setShowOverride] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  // Get formatted style display: "Brand - StyleNo"
  const getFormattedStyle = () => {
    if (!line.garmentId) return '';
    
    // Extract styleNo from garmentId (format: "StyleNo - Description" or just "StyleNo")
    const styleNo = line.garmentId.split(' - ')[0];
    
    // Look up the product to get the brand
    const product = products.find(p => p.styleNo === styleNo);
    
    if (product?.brand) {
      return `${product.brand} - ${styleNo}`;
    }
    
    // Fallback to just the styleNo if no brand found
    return styleNo;
  };

  const updateLineStatus = async (updates) => {
    try {
      let newStatus;

      // Calculate the new IN and CUT states
      const willBeIn = 'isIn' in updates ? updates.isIn : line.isIn;
      const willBeCut = 'isCut' in updates ? updates.isCut : line.isCut;
      const sample = isSampleLine(line);

      if ('printed' in updates) {
        if (updates.printed) {
          const blockReason = await reasonSpLineCannotPrint(db, line);
          if (blockReason) {
            alert(blockReason);
            return;
          }
          // Can only be printed if both IN and CUT are true
          if (!willBeIn || !willBeCut) {
            throw new Error('Cannot mark as printed until both IN and CUT are checked');
          }
          newStatus = LINE_STATUSES.COMPLETED;
        } else {
          // If un-printing, go back to READY_TO_PRINT since both IN and CUT must still be true
          newStatus = LINE_STATUSES.READY_TO_PRINT;
        }

        // Update the parent order's printed counts
        const orderRef = doc(db, 'orders', line.orderId);
        const updateData = {
          printedLines: updates.printed ? increment(1) : increment(-1),
          printedQuantity: updates.printed ? increment(line.totalQuantity) : increment(-line.totalQuantity)
        };
        await updateDoc(orderRef, updateData);
      } else if (lineIsFinalized(line)) {
        // A printed/completed line must never be downgraded by an IN/CUT toggle.
        newStatus = line.status;
      } else {
        // Handle IN/CUT status changes
        if (sample && willBeIn) {
          newStatus = LINE_STATUSES.COMPLETED;
        } else if (willBeIn && willBeCut) {
          newStatus = LINE_STATUSES.READY_TO_PRINT;
        } else if (lineHasBeenOrdered(line.status)) {
          if (willBeIn) {
            newStatus = LINE_STATUSES.IN_NOT_CUT;
          } else if (willBeCut) {
            newStatus = LINE_STATUSES.CUT_NOT_IN;
          } else {
            newStatus = LINE_STATUSES.ORDERED;
          }
        } else if (willBeCut && !willBeIn) {
          // Pre-order cut (e.g. film ready) — keep Ready To Order / New
          newStatus = line.status;
        } else {
          newStatus = line.status;
        }
      }

      // Describe this change for the line's activity log
      let logEvent = null;
      if ('printed' in updates) {
        logEvent = updates.printed ? 'Marked as Printed' : 'Unmarked Printed';
      } else if ('isIn' in updates) {
        logEvent = updates.isIn ? 'Marked as IN' : 'Unmarked IN';
      } else if ('isCut' in updates) {
        logEvent = updates.isCut ? 'Marked as CUT' : 'Unmarked CUT';
      }

      const updateData = {
        ...updates,
        status: newStatus,
        updatedAt: serverTimestamp(),
        // Folded into the existing write, so this adds no extra Firestore cost
        ...(logEvent ? activityLogAppend(logEvent, { from: line.status, to: newStatus }) : {})
      };

      // If this is an override, add a note
      if (showOverride) {
        updateData.statusOverride = true;
        updateData.overrideNote = 'Manual override by admin';
        updateData.overrideTimestamp = serverTimestamp();
      }

      const lineRef = doc(db, 'orderLines', line.id);
      await updateDoc(lineRef, updateData);

      // When IN or CUT is toggled, update order status from production lines (excludes Design Only)
      if (('isIn' in updates || 'isCut' in updates) && line.orderId) {
        try {
          const orderedOrLater = lineHasBeenOrdered(newStatus);
          if (orderedOrLater || ('isIn' in updates && updates.isIn)) {
            await syncOrderStatusFromLines(db, line.orderId);
          }
          if (sample && willBeIn) {
            await maybeFinalizeSampleOrderAfterReceive(db, line.orderId);
          }
        } catch (err) {
          console.warn('Could not update order status:', err);
        }
      }
      
      onLineUpdate(line.id, { ...updates, status: newStatus });
      setShowOverride(false);
    } catch (error) {
      console.error('Error updating line status:', error);
      alert(error.message || 'Failed to update status');
    }
  };

  const handlePrintedToggle = () => {
    // Can only mark as printed if both IN and CUT are done
    if (!line.isIn || !line.isCut) {
      alert('Items must be both IN and CUT before marking as printed');
      return;
    }
    updateLineStatus({
      printed: !line.printed
    });
  };

  const handleInToggle = () => {
    updateLineStatus({
      isIn: !line.isIn
    });
  };

  const handleCutToggle = () => {
    updateLineStatus({
      isCut: !line.isCut
    });
  };

  const handleTypeChange = (e) => {
    updateLineStatus({
      type: getDbType(e.target.value)
    });
  };

  // Check if line is ready for printing
  const isReadyForPrinting = line.isIn && line.isCut;

  // Update size breakdown to use sortSizes
  const renderSizeBreakdown = () => {
    if (!line.sizes) return null;
    
    const sortedSizes = Object.entries(line.sizes)
      .sort(([sizeA], [sizeB]) => sortSizes(sizeA, sizeB));

    return (
      <SizeBreakdown>
        <SizeRow>
          {sortedSizes.map(([size]) => (
            <SizeUnit key={`header-${size}`}>
              <span className="size-label">{size}</span>
            </SizeUnit>
          ))}
        </SizeRow>
        <SizeRow>
          {sortedSizes.map(([size, qty]) => (
            <SizeUnit key={`qty-${size}`}>
              <span className="size-qty">{qty}</span>
            </SizeUnit>
          ))}
        </SizeRow>
      </SizeBreakdown>
    );
  };

  // DTF/DTG: count activated print locations (from printLocations checkboxes). Screen/other: count ink colors.
  const isDTFOrDTG = () => {
    const t = String(line.type || '').toLowerCase();
    return t === 'dtf' || t === 'dtg';
  };

  const getInkColorCounts = () => {
    if (isDTFOrDTG()) {
      const locs = line.printLocations || [];
      let front, back, other;
      if (locs.length > 0) {
        front = locs.some(l => String(l).toLowerCase() === 'front') ? 1 : 0;
        back = locs.some(l => String(l).toLowerCase() === 'back') ? 1 : 0;
        other = locs.some(l => String(l).toLowerCase() === 'other') ? 1 : 0;
      } else {
        front = (Array.isArray(line.frontInkColors) && line.frontInkColors.length > 0) ? 1 : 0;
        back = (Array.isArray(line.backInkColors) && line.backInkColors.length > 0) ? 1 : 0;
        other = (Array.isArray(line.otherInkColors) && line.otherInkColors.length > 0) ? 1 : 0;
      }
      return `${front} / ${back} / ${other}`;
    }
    const frontCount = line.frontInkColors?.length || 0;
    const backCount = line.backInkColors?.length || 0;
    const otherCount = line.otherInkColors?.length || 0;
    return `${frontCount} / ${backCount} / ${otherCount}`;
  };

  const getStatusDisplay = () => {
    const sample = isSampleLine(line);
    if (sample && line.status === LINE_STATUSES.COMPLETED) return 'Sample (Ready)';
    if (sample && line.isIn) return 'Sample (In)';
    if (sample && line.status === LINE_STATUSES.ORDERED) return 'Sample (Ordered)';

    // Label is derived from the same effective status the badge color uses, so the two
    // can never disagree (no more grey "Ready to Print").
    switch (getEffectiveLineStatus(line)) {
      case LINE_STATUSES.PRINTED:
      case LINE_STATUSES.COMPLETED:
        return 'Printed';
      case LINE_STATUSES.READY_TO_PRINT:
        return 'Ready to Print';
      case LINE_STATUSES.IN_NOT_CUT:
        return 'IN not Cut';
      case LINE_STATUSES.CUT_NOT_IN:
        return 'CUT not IN';
      case LINE_STATUSES.ORDERED:
        return line.orderProvidedGoods ? 'Provided Goods' : 'Ordered';
      default:
        return 'New';
    }
  };

  return (
    <>
      <LineContainer>
        {/* Column 1: Printed Checkbox */}
        <Column onClick={(e) => e.stopPropagation()}>
          <CheckboxContainer>
            <CustomCheckbox
              type="checkbox"
              checked={line.printed}
              onChange={handlePrintedToggle}
              disabled={!isReadyForPrinting}
            />
          </CheckboxContainer>
        </Column>

        {/* Column 2: Invoice, Due Date & Status */}
        <Column>
          <Label>Invoice</Label>
          <Value>{line.orderNumber || 'N/A'}</Value>
          <Label>Due</Label>
          <Value>{formatDate(line.dueDate)}</Value>
          <CheckboxContainer onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox
              type="checkbox"
              checked={line.isIn}
              onChange={handleInToggle}
            />
            <CheckboxLabel>IN</CheckboxLabel>
          </CheckboxContainer>
          <CheckboxContainer onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox
              type="checkbox"
              checked={line.isCut}
              onChange={handleCutToggle}
            />
            <CheckboxLabel>CUT</CheckboxLabel>
          </CheckboxContainer>
        </Column>

        {/* Column 3: Thumbnail */}
        <MockupContainer 
          noImage={!getMockupUrl(line)}
          onClick={(e) => { e.stopPropagation(); getMockupUrl(line) && setSelectedArtwork(getMockupUrl(line)); }}
        >
          {getMockupUrl(line) && (
            <MockupImage 
              src={getMockupUrl(line)} 
              alt={line.designName}
            />
          )}
        </MockupContainer>

        {/* Column 4: Design & Client */}
        <Column>
          <Label>Design</Label>
          <Value>{line.designName}</Value>
          <Label>Client</Label>
          <Value>{line.customerName}</Value>
        </Column>

        {/* Column 5: Type, Style & Color */}
        <Column>
          <Label>Type</Label>
          <Select 
            value={getDisplayType(line.type)}
            onChange={handleTypeChange}
            onClick={(e) => e.stopPropagation()}
          >
            {Object.entries(LINE_TYPES).map(([key, value]) => (
              <option key={key} value={value}>
                {value}
              </option>
            ))}
          </Select>
          <Label>Style</Label>
          <Value>{getFormattedStyle()}</Value>
          <Label>Color</Label>
          <Value>{line.garmentColor}</Value>
        </Column>

        {/* Column 6: Size Breakdown */}
        <Column>
          <Label>Sizes</Label>
          <SizeBreakdown>
            {renderSizeBreakdown()}
          </SizeBreakdown>
        </Column>

        {/* Column 7: Total and Colors/Films */}
        <Column>
          <Label>Total</Label>
          <Value>{line.totalQuantity}</Value>
          <Label>{isDTFOrDTG() ? 'Films F/B/O' : 'Colors F/B/O'}</Label>
          <InkColorCount>{getInkColorCounts()}</InkColorCount>
        </Column>

        {/* Column 8: Status Badge */}
        <Column>
          <StatusBadge status={getEffectiveLineStatus(line)}>
            {getStatusDisplay()}
          </StatusBadge>
        </Column>

        {/* Add override checkbox for admin users */}
        {isAdmin && (
          <Column>
            <CheckboxContainer>
              <CustomCheckbox
                type="checkbox"
                checked={showOverride}
                onChange={(e) => setShowOverride(e.target.checked)}
              />
              <CheckboxLabel>Override Status</CheckboxLabel>
            </CheckboxContainer>
          </Column>
        )}
      </LineContainer>

      {/* Add Large Artwork Modal */}
      {selectedArtwork && (
        <LargeArtworkModal onClick={() => setSelectedArtwork(null)}>
          <LargeArtworkContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedArtwork(null)}>
              <FaTimes />
            </CloseButton>
            <LargeArtworkImage src={selectedArtwork} alt="Large artwork preview" />
          </LargeArtworkContent>
        </LargeArtworkModal>
      )}
    </>
  );
};

export default LineItemCompact; 